import React, { useState, useRef, useEffect } from 'react';
import { Lock, X, ShieldAlert, KeyRound, CheckCircle2 } from 'lucide-react';

interface PinUnlockModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (pin: string) => boolean;
}

export const PinUnlockModal: React.FC<PinUnlockModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [digits, setDigits] = useState(['', '', '', '']);
  const [errorMsg, setErrorMsg] = useState('');
  const [isShaking, setIsShaking] = useState(false);
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (isOpen) {
      setDigits(['', '', '', '']);
      setErrorMsg('');
      setIsShaking(false);
      // Auto-focus no primeiro dígito
      setTimeout(() => {
        inputsRef.current[0]?.focus();
      }, 100);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (index: number, value: string) => {
    setErrorMsg('');
    const cleanVal = value.replace(/\D/g, '');

    // Se o usuário colou 4 dígitos de uma vez
    if (cleanVal.length > 1) {
      const pasted = cleanVal.slice(0, 4).split('');
      const newDigits = ['', '', '', ''];
      pasted.forEach((ch, i) => {
        if (i < 4) newDigits[i] = ch;
      });
      setDigits(newDigits);
      if (pasted.length === 4) {
        verifyPin(newDigits.join(''));
      } else {
        const nextIdx = Math.min(3, pasted.length);
        inputsRef.current[nextIdx]?.focus();
      }
      return;
    }

    const singleDigit = cleanVal.slice(-1);
    const newDigits = [...digits];
    newDigits[index] = singleDigit;
    setDigits(newDigits);

    // Avança para o próximo input
    if (singleDigit && index < 3) {
      inputsRef.current[index + 1]?.focus();
    }

    // Se preencheu todos os 4 dígitos, valida automaticamente
    if (singleDigit && index === 3 && newDigits.every(d => d !== '')) {
      verifyPin(newDigits.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (!digits[index] && index > 0) {
        inputsRef.current[index - 1]?.focus();
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      verifyPin(digits.join(''));
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const verifyPin = (fullPin: string) => {
    if (fullPin.length < 4) {
      setErrorMsg('Digite os 4 dígitos do PIN.');
      triggerShake();
      return;
    }

    const ok = onSuccess(fullPin);
    if (!ok) {
      setErrorMsg('PIN incorreto. Tente novamente.');
      triggerShake();
      setDigits(['', '', '', '']);
      inputsRef.current[0]?.focus();
    }
  };

  const triggerShake = () => {
    setIsShaking(true);
    setTimeout(() => setIsShaking(false), 500);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div
        className={`bg-white dark:bg-slate-800 rounded-3xl max-w-sm w-full p-6 shadow-2xl border border-slate-200 dark:border-slate-700 transition-all ${
          isShaking ? 'animate-bounce' : ''
        }`}
      >
        {/* Cabeçalho */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="p-2 rounded-2xl bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
              <Lock className="w-5 h-5" />
            </span>
            <div>
              <h3 className="text-base font-black text-slate-900 dark:text-slate-50">
                Desbloquear Acesso
              </h3>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">
                Segurança do Modo Valores Ocultos
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-slate-50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Informação */}
        <p className="text-xs text-slate-600 dark:text-slate-300 mb-5 leading-relaxed text-center">
          Digite o <strong>PIN de 4 dígitos</strong> da sua ótica para desocultar os valores e liberar a edição e exclusão de registros.
        </p>

        {/* Inputs de 4 Dígitos */}
        <div className="flex items-center justify-center gap-3 mb-4">
          {digits.map((digit, idx) => (
            <input
              key={idx}
              ref={(el) => {
                inputsRef.current[idx] = el;
              }}
              type="password"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(idx, e.target.value)}
              onKeyDown={(e) => handleKeyDown(idx, e)}
              className={`w-12 h-14 text-center font-mono text-2xl font-black rounded-2xl border bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-50 outline-none transition-all shadow-inner ${
                errorMsg
                  ? 'border-rose-500 focus:ring-2 focus:ring-rose-500/30'
                  : 'border-slate-300 dark:border-slate-700 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/30'
              }`}
            />
          ))}
        </div>

        {/* Mensagem de Erro */}
        {errorMsg && (
          <div className="flex items-center justify-center gap-1.5 text-xs text-rose-600 dark:text-rose-400 font-bold mb-4 animate-in fade-in">
            <ShieldAlert className="w-4 h-4 shrink-0" />
            <span>{errorMsg}</span>
          </div>
        )}

        {/* Dica amigável */}
        <p className="text-[10px] text-slate-400 dark:text-slate-500 text-center mb-5">
          * O PIN padrão inicial é <strong>1234</strong> e pode ser alterado em <em>Configurações da Loja</em>.
        </p>

        {/* Botões de Ação */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-xs font-bold hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => verifyPin(digits.join(''))}
            className="flex-1 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-xs font-black shadow-md shadow-amber-500/30 transition-all hover:scale-105 flex items-center justify-center gap-1.5"
          >
            <KeyRound className="w-4 h-4" />
            <span>Desbloquear</span>
          </button>
        </div>
      </div>
    </div>
  );
};
